import React, { useEffect, useMemo, useState } from "react"
import { NavLink } from "react-router-dom"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./reopened-conversions.scss"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000
const ONE_DAY_MS = 24 * 60 * 60 * 1000

const getDefaultDateRange = () => {
	const end = moment().endOf("day").toDate()
	const start = moment().subtract(30, "days").startOf("day").toDate()
	return { startDate: start, endDate: end }
}

const toMillis = (ts) => {
	if (!ts) return null
	if (typeof ts === "number") return ts
	if (ts?.seconds) return ts.seconds * 1000
	return null
}

const formatDurationFromSeconds = (seconds) => {
	if (seconds === null || seconds === undefined) return ""
	const safeSeconds = Math.max(0, Math.round(seconds))

	if (safeSeconds < 60) {
		return `${safeSeconds} second${safeSeconds === 1 ? "" : "s"}`
	}

	const totalMinutes = Math.floor(safeSeconds / 60)
	if (totalMinutes < 60) {
		return `${totalMinutes} minute${totalMinutes === 1 ? "" : "s"}`
	}

	const totalHours = Math.floor(totalMinutes / 60)
	const remainingMinutes = totalMinutes % 60
	if (totalHours < 24) {
		if (remainingMinutes > 0) {
			return `${totalHours} hour${totalHours === 1 ? "" : "s"} ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}`
		}
		return `${totalHours} hour${totalHours === 1 ? "" : "s"}`
	}

	const days = Math.floor(totalHours / 24)
	const remainingHours = totalHours % 24
	const parts = [`${days} day${days === 1 ? "" : "s"}`]

	if (remainingHours > 0) {
		parts.push(`${remainingHours} hour${remainingHours === 1 ? "" : "s"}`)
	}
	if (remainingMinutes > 0) {
		parts.push(`${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}`)
	}
	return parts.join(" ")
}

export default function ReopenedConversions() {
	const defaults = getDefaultDateRange()
	const [startDate, setStartDate] = useState(defaults.startDate)
	const [endDate, setEndDate] = useState(defaults.endDate)

	const [lastFetchedStart, setLastFetchedStart] = useState(null)
	const [lastFetchedEnd, setLastFetchedEnd] = useState(null)

	const [loading, setLoading] = useState(false)
	const [rows, setRows] = useState([])
	const [clientsById, setClientsById] = useState({})
	const [agentsById, setAgentsById] = useState({})

	const datesChanged = useMemo(() => {
		if (!lastFetchedStart || !lastFetchedEnd) return true
		return moment(startDate).format("YYYY-MM-DD") !== moment(lastFetchedStart).format("YYYY-MM-DD") || moment(endDate).format("YYYY-MM-DD") !== moment(lastFetchedEnd).format("YYYY-MM-DD")
	}, [startDate, endDate, lastFetchedStart, lastFetchedEnd])

	useEffect(() => {
		// Initial load
		fetchData()
	}, [])

	const handleStartDateChange = (milliseconds) => {
		setStartDate(moment(milliseconds).startOf("day").toDate())
	}

	const handleEndDateChange = (milliseconds) => {
		setEndDate(moment(milliseconds).endOf("day").toDate())
	}

	const hydrateClientNames = async (clientIds) => {
		const unique = [...new Set((clientIds || []).filter(Boolean))]
		const missing = unique.filter((id) => !clientsById[id])
		if (missing.length === 0) return

		const fetched = await Promise.all(
			missing.map(async (id) => {
				const snap = await db.doc(`clients/${id}`).get()
				const data = snap.exists ? snap.data() : null
				const name = data?.first_name ? `${data.first_name} ${data.last_name}` : id
				return [id, name]
			})
		)

		setClientsById((prev) => {
			const next = { ...prev }
			fetched.forEach(([id, name]) => {
				next[id] = name
			})
			return next
		})
	}

	const hydrateAgentNames = async (agentIds) => {
		const unique = [...new Set((agentIds || []).filter(Boolean))]
		const missing = unique.filter((id) => !agentsById[id])
		if (missing.length === 0) return

		const fetched = await Promise.all(
			missing.map(async (id) => {
				const snap = await db.doc(`users/${id}`).get()
				const data = snap.exists ? snap.data() : null
				const name = data?.first_name ? `${data.first_name} ${data.last_name}` : id
				return [id, name]
			})
		)

		setAgentsById((prev) => {
			const next = { ...prev }
			fetched.forEach(([id, name]) => {
				next[id] = name
			})
			return next
		})
	}

	const fetchData = async () => {
		setLoading(true)
		setRows([])

		try {
			const startMs = moment(startDate).valueOf()
			const endMs = moment(endDate).valueOf()

			const startTs = firebase.firestore.Timestamp.fromMillis(startMs)
			const endTs = firebase.firestore.Timestamp.fromMillis(endMs)
			const bookingsEndTs = firebase.firestore.Timestamp.fromMillis(endMs + ONE_DAY_MS)

			const [removedSnap, bookingsSnap] = await Promise.all([
				db.collection("enquiries_removed").where("created", ">=", startTs).where("created", "<=", endTs).orderBy("created", "desc").get(),
				db.collection("bookings").where("enquiry.created", ">=", startTs).where("enquiry.created", "<=", bookingsEndTs).get(),
			])

			const bookings = bookingsSnap.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}))

			const bookingsByClient = new Map()
			for (const booking of bookings) {
				const clientId = booking.client
				if (!clientId) continue
				if (!bookingsByClient.has(clientId)) bookingsByClient.set(clientId, [])
				bookingsByClient.get(clientId).push(booking)
			}

			const flagged = []

			removedSnap.docs.forEach((doc) => {
				const data = doc.data()
				const removedAtMs = toMillis(data.created)
				const enquiredAtMs = toMillis(data.enquired) ?? toMillis(data.enquiry?.created)
				const clientId = data.enquiry?.client

				if (!removedAtMs || !enquiredAtMs || !clientId) return

				const ageAtRemovalMs = removedAtMs - enquiredAtMs
				if (ageAtRemovalMs < THIRTY_DAYS_MS) return

				const clientBookings = bookingsByClient.get(clientId) || []
				const matchingBooking = clientBookings.find((b) => {
					const bookingEnquiryCreatedMs = toMillis(b.enquiry?.created)
					if (!bookingEnquiryCreatedMs) return false
					return bookingEnquiryCreatedMs >= removedAtMs && bookingEnquiryCreatedMs <= removedAtMs + ONE_DAY_MS
				})

				if (!matchingBooking) return

				const bookingEnquiryCreatedMs = toMillis(matchingBooking.enquiry?.created)
				const deltaSeconds = bookingEnquiryCreatedMs ? Math.round((bookingEnquiryCreatedMs - removedAtMs) / 1000) : null
				const secondsToBooking =
					bookingEnquiryCreatedMs && toMillis(matchingBooking.created)
						? Math.round((toMillis(matchingBooking.created) - bookingEnquiryCreatedMs) / 1000)
						: null

				flagged.push({
					clientId,
					removedEnquiry: data,
					removedAtMs,
					enquiredAtMs,
					ageAtRemovalDays: Math.floor(ageAtRemovalMs / (24 * 60 * 60 * 1000)),
					bookingId: matchingBooking.id,
					bookingRef: matchingBooking.reference,
					bookingAgent: matchingBooking.agent,
					bookingCreatedMs: toMillis(matchingBooking.created),
					bookingEnquiryCreatedMs,
					deltaSeconds,
					secondsToBooking,
					newEnquiryLocation: matchingBooking.enquiry?.location || "",
					newEnquirySite: matchingBooking.enquiry?.site || "",
				})
			})

			flagged.sort((a, b) => b.removedAtMs - a.removedAtMs)

			setRows(flagged)
			await Promise.all([hydrateClientNames(flagged.map((x) => x.clientId)), hydrateAgentNames(flagged.map((x) => x.bookingAgent))])
			setLastFetchedStart(startDate)
			setLastFetchedEnd(endDate)
		} catch (err) {
			console.error("Error fetching reopened conversions:", err)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Reopened Enquiries</h1>
			</Title>

			<div className="reopened-conversions-controls">
				<div className="date-picker-group">
					<div className="date-picker-wrapper">
						<label>From:</label>
						<Datepicker
							placeholder="Start Date"
							value={moment(startDate).valueOf()}
							onSelect={handleStartDateChange}
						/>
					</div>
					<div className="date-picker-wrapper">
						<label>To:</label>
						<Datepicker
							placeholder="End Date"
							value={moment(endDate).valueOf()}
							onSelect={handleEndDateChange}
						/>
					</div>

					{datesChanged && (
						<Button
							label="Update"
							small={true}
							onClick={fetchData}
							loading={loading}
							loadingText="Updating..."
						/>
					)}
				</div>

				<div className="selected-range-info">
					<span>
						Selected: {moment(startDate).format("MMM Do")} - {moment(endDate).format("MMM Do YYYY")}
					</span>
				</div>
			</div>

			<Table
				className="reopened-conversions-table"
				headings={[
					"Agent",
					"Booking ref",
					"Client",
					"Old enquiry removed",
					"Age at removal",
					"Converted enquiry created",
					"Reopened after",
					"Booked after",
					"Old enquiry location/site",
					"New enquiry location/site",
				]}
				noResults={!loading && rows.length === 0}
				noResultsMessage="No reopened conversions found in this range">
				{loading && (
					<tr>
						<td colSpan={10}>
							<div className="reopened-conversions-loading">
								<small>Loading…</small>
							</div>
						</td>
					</tr>
				)}

				{!loading &&
					rows.map((r) => {
						const location = r.removedEnquiry?.enquiry?.location || ""
						const site = r.removedEnquiry?.site || r.removedEnquiry?.enquiry?.site || ""
						const newLocation = r.newEnquiryLocation || ""
						const newSite = r.newEnquirySite || ""
						const clientName = clientsById[r.clientId] || r.clientId
						const agentName = r.bookingAgent ? agentsById[r.bookingAgent] || r.bookingAgent : ""
						const removedToNewEnquiryText = formatDurationFromSeconds(r.deltaSeconds)
						const newEnquiryToBookingText = formatDurationFromSeconds(r.secondsToBooking)
						const ageAtRemovalText = `${r.ageAtRemovalDays} day${r.ageAtRemovalDays === 1 ? "" : "s"}`
						return (
							<tr key={`${r.clientId}-${r.bookingId}-${r.removedAtMs}`}>
								<td>
									{agentName}
								</td>
								<td>
									<NavLink to={`/booking/${r.bookingId}`}>{r.bookingRef ? `${r.bookingRef}` : r.bookingId}</NavLink>
								</td>
								<td>
									<NavLink to={`/client/${r.clientId}`}>{clientName}</NavLink>
								</td>
								<td>{moment(r.removedAtMs).format("DD/MM/YYYY HH:mm:ss")}</td>
								<td>{ageAtRemovalText}</td>
								<td>{r.bookingEnquiryCreatedMs ? moment(r.bookingEnquiryCreatedMs).format("DD/MM/YYYY HH:mm:ss") : ""}</td>
								<td>{removedToNewEnquiryText}</td>
								<td>{newEnquiryToBookingText}</td>
								<td>
									{location}
									{site ? ` (${site})` : ""}
								</td>
								<td>
									{newLocation}
									{newSite ? ` (${newSite})` : ""}
								</td>
							</tr>
						)
					})}
			</Table>
		</Tile>
	)
}


